import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import WhatsAppMockup from '../components/WhatsAppMockup';
import { 
  MessageCircle, 
  Bot, 
  CheckCircle, 
  Clock, 
  Calendar, 
  TrendingUp,
  Footprints,
  Sparkles,
  Heart,
  Smile,
  Stethoscope,
  ArrowRight,
  Menu,
  X,
  Globe,
  Mail,
  Utensils,
  ShoppingBag,
  GraduationCap,
  Car,
  Home,
  Wrench,
  Zap,
  Code,
  BarChart3,
  ExternalLink
} from 'lucide-react';

const Landing = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasAnimated, setHasAnimated] = useState({});

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.nav-content')) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const observerOptions = { threshold: 0.1, rootMargin: '0px 0px -100px 0px' };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasAnimated[entry.target.id]) {
          entry.target.classList.add('animate-in');
          setHasAnimated(prev => ({ ...prev, [entry.target.id]: true }));
        }
      });
    }, observerOptions);
    document.querySelectorAll('.fade-in-section').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [hasAnimated]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const whatsappLink = "https://wa.me/523349731369";
  const whatsappPlanLink = (planName) => `https://wa.me/523349731369?text=Hola,%20me%20interesa%20el%20plan%20${planName}%20de%20Progress%20Digital`;

  const sectores = [
    { icon: Utensils, label: "Restaurantes" },
    { icon: Footprints, label: "Podólogos" },
    { icon: Sparkles, label: "Estéticas & Spas" },
    { icon: Smile, label: "Dentistas" },
    { icon: GraduationCap, label: "Escuelas" },
    { icon: Home, label: "Inmobiliarias" },
    { icon: Car, label: "Talleres Automotrices" },
    { icon: ShoppingBag, label: "Tiendas & Comercios" },
    { icon: Heart, label: "Clínicas & Salud" },
    { icon: Wrench, label: "Servicios en General" },
  ];

  const beneficios = [
    { icon: Clock, title: "Atención 24/7", description: "Tu negocio nunca duerme. El chatbot responde a cualquier hora, incluso fines de semana y días festivos.", stat: "24/7" },
    { icon: Calendar, title: "Citas y pedidos automáticos", description: "Sin intervención manual. El bot agenda, confirma y recuerda citas o pedidos por ti.", stat: "+500" },
    { icon: MessageCircle, title: "Responde en segundos", description: "Tus clientes obtienen respuesta inmediata. Sin tiempos de espera que los hagan irse con la competencia.", stat: "2s" },
    { icon: TrendingUp, title: "Más ventas, menos trabajo", description: "Automatiza el seguimiento de prospectos y cierra más ventas sin aumentar tu equipo.", stat: "3x" }
  ];

  const planes = [
    {
      nombre: "Básico",
      precio: "$799 MXN",
      precioUSD: "~$39 USD",
      features: [
        "Chatbot WhatsApp con IA",
        "Agendamiento o atención automática",
        "Hasta 300 conversaciones/mes",
        "1 número de WhatsApp",
        "Respuestas personalizadas a tu negocio",
        "Soporte por WhatsApp"
      ],
      popular: false
    },
    {
      nombre: "Pro",
      precio: "$1,499 MXN",
      precioUSD: "~$74 USD",
      features: [
        "Todo lo del plan Básico",
        "Hasta 800 conversaciones/mes",
        "Recordatorios automáticos",
        "Panel de gestión en tiempo real",
        "Integración con catálogo o menú",
        "Soporte prioritario"
      ],
      popular: true
    },
    {
      nombre: "Premium",
      precio: "$2,299 MXN",
      precioUSD: "~$114 USD",
      features: [
        "Todo lo del plan Pro",
        "Conversaciones ilimitadas",
        "50 mensajes de plantilla/mes",
        "Mensaje de bienvenida con imagen/video",
        "Integración con Google Calendar",
        "Automatizaciones personalizadas",
        "Onboarding asistido incluido",
        "Soporte prioritario"
      ],
      popular: false
    }
  ];

  const portfolio = [
    {
      title: "App de Invitación Digital",
      category: "Evento — Quinceañera",
      description: "App web con sobre animado, galería de fotos en tiempo real, confirmación de asistencia con QR y panel de administración privado.",
      tags: ["React", "Firebase", "Cloudinary", "Netlify"],
      color: "#00E5CC"
    },
    {
      title: "Chatbot de Ventas BodyLogic",
      category: "E-commerce — Suplementos",
      description: "Asistente de ventas por WhatsApp con IA para red de afiliados de suplementos. Responde dudas, recomienda productos y cierra ventas 24/7.",
      tags: ["n8n", "GPT-4o", "Chatwoot", "NocoDB"],
      color: "#00A8FF"
    },
    {
      title: "Sistema Multi-cliente WhatsApp",
      category: "SaaS — Automatización",
      description: "Infraestructura que sirve múltiples negocios desde un solo flujo, identificando clientes por número de teléfono y cargando configuración individual.",
      tags: ["n8n", "NocoDB", "WhatsApp API", "Coolify"],
      color: "#00E5CC"
    }
  ];

  return (
    <div className="landing-page">
      {/* Navbar */}
      <nav className="navbar">
        <div className="container-custom">
          <div className="nav-content">
            <div className="nav-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_c7eb3646-d34b-4b9e-a8c5-4fd4b2442497/artifacts/3ackggnp_Logo%20Progress%20Digital.png" 
                alt="Progress Digital" 
                className="logo-img"
              />
              <span className="logo-text">Progress Digital</span>
            </div>
            
            <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              <button onClick={() => scrollToSection('como-funciona')} className="nav-link">Cómo funciona</button>
              <button onClick={() => scrollToSection('sectores')} className="nav-link">Sectores</button>
              <button onClick={() => scrollToSection('portafolio')} className="nav-link">Portafolio</button>
              <button onClick={() => scrollToSection('precios')} className="nav-link">Precios</button>
              <button onClick={() => scrollToSection('contacto')} className="nav-link">Contacto</button>
              <Button onClick={() => window.open(whatsappLink, '_blank')} className="cta-button-nav">
                Quiero mi chatbot
              </Button>
            </div>

            <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="animated-grid"></div>
        <div className="container-custom">
          <div className="hero-content">
            <div className="hero-text" style={{ transform: `translateY(${scrollY * 0.15}px)` }}>
              <div className="hero-badge">
                <Zap size={14} />
                <span>Automatización con IA para negocios en México</span>
              </div>
              <h1 className="hero-title gradient-text">
                Automatiza tu negocio con IA, sin complicaciones
              </h1>
              <p className="hero-subtitle">
                Chatbots de WhatsApp, apps de eventos, integraciones y automatizaciones a medida. Soluciones reales para negocios que quieren crecer sin contratar más personal.
              </p>
              <div className="hero-buttons">
                <Button onClick={() => window.open(whatsappLink, '_blank')} className="cta-button-primary" size="lg">
                  Quiero mi chatbot
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button onClick={() => scrollToSection('como-funciona')} variant="outline" className="cta-button-secondary" size="lg">
                  Ver cómo funciona
                </Button>
              </div>
              <div className="hero-stats">
                <div className="hero-stat">
                  <span className="stat-number gradient-text">24/7</span>
                  <span className="stat-label">Disponibilidad</span>
                </div>
                <div className="hero-stat-divider" />
                <div className="hero-stat">
                  <span className="stat-number gradient-text">2s</span>
                  <span className="stat-label">Tiempo de respuesta</span>
                </div>
                <div className="hero-stat-divider" />
                <div className="hero-stat">
                  <span className="stat-number gradient-text">+10</span>
                  <span className="stat-label">Industrias</span>
                </div>
              </div>
            </div>
            
            <div className="hero-image" style={{ transform: `translateY(${scrollY * 0.1}px)` }}>
              <WhatsAppMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Cómo Funciona */}
      <section id="como-funciona" className="section-padding fade-in-section">
        <div className="container-custom">
          <h2 className="section-title gradient-text">¿Cómo funciona?</h2>
          <div className="steps-container">
            <div className="step-card">
              <div className="step-icon"><MessageCircle size={40} /></div>
              <h3 className="step-title">Tu cliente escribe por WhatsApp</h3>
              <p className="step-description">A cualquier hora, desde donde esté. Sin apps extra ni instalaciones.</p>
            </div>
            <div className="step-arrow"><ArrowRight size={32} /></div>
            <div className="step-card">
              <div className="step-icon"><Bot size={40} /></div>
              <h3 className="step-title">La IA responde y gestiona</h3>
              <p className="step-description">Resuelve dudas, agenda citas, toma pedidos o califica prospectos automáticamente.</p>
            </div>
            <div className="step-arrow"><ArrowRight size={32} /></div>
            <div className="step-card">
              <div className="step-icon"><CheckCircle size={40} /></div>
              <h3 className="step-title">Tú solo supervises</h3>
              <p className="step-description">Recibe un resumen, revisa tu panel y enfócate en lo que realmente importa.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sectores */}
      <section id="sectores" className="section-padding fade-in-section">
        <div className="container-custom">
          <h2 className="section-title gradient-text">¿Para qué tipo de negocio?</h2>
          <p className="section-subtitle">Si tienes clientes, tienes algo que automatizar. Trabajamos con cualquier giro.</p>
          <div className="sectores-grid">
            {sectores.map((sector, index) => {
              const Icon = sector.icon;
              return (
                <div key={index} className="sector-card">
                  <Icon size={48} className="sector-icon" />
                  <p className="sector-label">{sector.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="section-padding fade-in-section">
        <div className="container-custom">
          <h2 className="section-title gradient-text">Lo que hacemos</h2>
          <div className="servicios-grid">
            <div className="servicio-card servicio-featured">
              <div className="servicio-icon-wrap">
                <MessageCircle size={36} />
              </div>
              <h3 className="servicio-title">Chatbots de WhatsApp con IA</h3>
              <p className="servicio-desc">Automatiza la atención al cliente, agendamiento, ventas y soporte en el canal que tus clientes ya usan.</p>
              <ul className="servicio-list">
                <li>✓ Respuestas 24/7 personalizadas</li>
                <li>✓ Agendamiento automático de citas</li>
                <li>✓ Catálogos y toma de pedidos</li>
                <li>✓ Calificación de prospectos</li>
              </ul>
            </div>
            <div className="servicio-card">
              <div className="servicio-icon-wrap">
                <Sparkles size={36} />
              </div>
              <h3 className="servicio-title">Apps de Eventos e Invitaciones</h3>
              <p className="servicio-desc">Invitaciones digitales interactivas con QR, galería en tiempo real, confirmación de asistencia y panel de administración.</p>
              <ul className="servicio-list">
                <li>✓ Diseño personalizado</li>
                <li>✓ Galería de fotos en vivo</li>
                <li>✓ RSVP con QR</li>
                <li>✓ Panel de administración</li>
              </ul>
            </div>
            <div className="servicio-card">
              <div className="servicio-icon-wrap">
                <Code size={36} />
              </div>
              <h3 className="servicio-title">Automatizaciones & Integraciones</h3>
              <p className="servicio-desc">Conectamos tus herramientas existentes y automatizamos procesos repetitivos. CRM, calendarios, hojas de cálculo y más.</p>
              <ul className="servicio-list">
                <li>✓ Flujos con n8n / Make</li>
                <li>✓ Integración de APIs</li>
                <li>✓ Google Sheets / Calendar</li>
                <li>✓ Notificaciones automáticas</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section id="beneficios" className="section-padding fade-in-section">
        <div className="container-custom">
          <h2 className="section-title gradient-text">¿Por qué automatizar?</h2>
          <div className="beneficios-grid">
            {beneficios.map((beneficio, index) => {
              const Icon = beneficio.icon;
              return (
                <Card key={index} className="beneficio-card">
                  <CardContent className="beneficio-content">
                    <div className="beneficio-header">
                      <Icon size={32} className="beneficio-icon" />
                      <span className="beneficio-stat gradient-text">{beneficio.stat}</span>
                    </div>
                    <h3 className="beneficio-title">{beneficio.title}</h3>
                    <p className="beneficio-description">{beneficio.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portafolio */}
      <section id="portafolio" className="section-padding fade-in-section">
        <div className="container-custom">
          <h2 className="section-title gradient-text">Proyectos reales</h2>
          <p className="section-subtitle">No somos una agencia de ideas. Aquí hay resultados concretos.</p>
          <div className="portfolio-grid">
            {portfolio.map((item, index) => (
              <div key={index} className="portfolio-card" style={{ '--accent-color': item.color }}>
                <div className="portfolio-category">{item.category}</div>
                <h3 className="portfolio-title">{item.title}</h3>
                <p className="portfolio-desc">{item.description}</p>
                <div className="portfolio-tags">
                  {item.tags.map((tag, i) => (
                    <span key={i} className="portfolio-tag">{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Precios */}
      <section id="precios" className="section-padding fade-in-section">
        <div className="container-custom">
          <h2 className="section-title gradient-text">Planes y Precios</h2>
          <p className="section-subtitle">Para chatbots de WhatsApp con suscripción mensual. Apps y automatizaciones se cotizan por proyecto.</p>
          <div className="pricing-grid">
            {planes.map((plan, index) => (
              <Card key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <Badge className="popular-badge">Más Popular</Badge>
                )}
                <CardContent className="pricing-content">
                  <h3 className="plan-nombre">{plan.nombre}</h3>
                  <div className="plan-precio">
                    <span className="precio-principal gradient-text">{plan.precio}</span>
                    <span className="precio-usd">{plan.precioUSD} /mes</span>
                  </div>
                  <ul className="plan-features">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="feature-item">
                        <CheckCircle size={20} className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => window.open(whatsappPlanLink(plan.nombre), '_blank')} className="plan-button">
                    Empezar ahora
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="setup-note">Todos los planes incluyen setup único de $800 MXN • Apps y automatizaciones se cotizan por proyecto</p>
        </div>
      </section>

      {/* Testimonios */}
      <section id="testimonios" className="section-padding fade-in-section">
        <div className="container-custom">
          <h2 className="section-title gradient-text">Lo que dicen nuestros clientes</h2>
          <div className="testimonios-grid">
            <Card className="testimonio-card">
              <CardContent className="testimonio-content">
                <div className="testimonio-avatar">
                  <div className="avatar-circle" style={{ background: 'linear-gradient(135deg, #00A8FF, #00E5CC)' }}>
                    <span className="avatar-initial">MC</span>
                  </div>
                </div>
                <p className="testimonio-text">
                  "Desde que implementamos el chatbot, nuestras citas aumentaron un 40%. Ya no perdemos clientes por no contestar a tiempo."
                </p>
                <div className="testimonio-author">
                  <p className="author-name">Dra. María Contreras</p>
                  <p className="author-role">Clínica Podológica, Guadalajara</p>
                </div>
              </CardContent>
            </Card>
            <Card className="testimonio-card">
              <CardContent className="testimonio-content">
                <div className="testimonio-avatar">
                  <div className="avatar-circle" style={{ background: 'linear-gradient(135deg, #00E5CC, #00A8FF)' }}>
                    <span className="avatar-initial">JR</span>
                  </div>
                </div>
                <p className="testimonio-text">
                  "El sistema es increíble. Mis clientes pueden agendar a cualquier hora y yo solo reviso mi panel. Me ahorró horas cada semana."
                </p>
                <div className="testimonio-author">
                  <p className="author-name">Jessica Ramírez</p>
                  <p className="author-role">Estética Bella Piel, Monterrey</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="cta-final-section fade-in-section">
        <div className="container-custom">
          <div className="cta-final-content">
            <h2 className="cta-final-title gradient-text">¿Listo para automatizar tu negocio?</h2>
            <p className="cta-final-subtitle">
              Cuéntanos qué necesitas y en 24 horas te mostramos cómo lo resolvemos.
            </p>
            <div className="cta-final-buttons">
              <Button onClick={() => window.open(whatsappLink, '_blank')} className="cta-button-large" size="lg">
                Hablar con un asesor
                <ArrowRight className="ml-2" size={24} />
              </Button>
              <Button onClick={() => window.open('mailto:progressdigitalcompany@protonmail.com', '_blank')} className="cta-button-large-outline" size="lg">
                Enviar correo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" className="footer">
        <div className="container-custom">
          <div className="footer-content">
            <div className="footer-column footer-brand-col">
              <div className="footer-logo-text gradient-text">Progress Digital</div>
              <p className="footer-tagline">Automatización inteligente para negocios que quieren crecer sin complicarse.</p>
              <div className="footer-social">
                <a href="https://wa.me/523349731369" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="WhatsApp">
                  <MessageCircle size={20} />
                </a>
                <a href="mailto:progressdigitalcompany@protonmail.com" className="social-icon" aria-label="Email">
                  <Mail size={20} />
                </a>
                <a href="https://progressdigital.company" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Website">
                  <Globe size={20} />
                </a>
              </div>
            </div>
            
            <div className="footer-column">
              <h4 className="footer-column-title">Navegación</h4>
              <div className="footer-links-col">
                <button onClick={() => scrollToSection('como-funciona')} className="footer-link-btn">Cómo funciona</button>
                <button onClick={() => scrollToSection('sectores')} className="footer-link-btn">Sectores</button>
                <button onClick={() => scrollToSection('portafolio')} className="footer-link-btn">Portafolio</button>
                <button onClick={() => scrollToSection('precios')} className="footer-link-btn">Precios</button>
                <button onClick={() => scrollToSection('contacto')} className="footer-link-btn">Contacto</button>
              </div>
            </div>
            
            <div className="footer-column">
              <h4 className="footer-column-title">Contacto</h4>
              <div className="footer-contact">
                <a href="https://progressdigital.company" target="_blank" rel="noopener noreferrer" className="contact-item">
                  <Globe size={18} className="contact-icon" />
                  <span>progressdigital.company</span>
                </a>
                <a href="mailto:progressdigitalcompany@protonmail.com" className="contact-item">
                  <Mail size={18} className="contact-icon" />
                  <span>progressdigitalcompany@protonmail.com</span>
                </a>
                <a href="https://wa.me/523349731369" target="_blank" rel="noopener noreferrer" className="contact-item">
                  <MessageCircle size={18} className="contact-icon" />
                  <span>+52 3349731369</span>
                </a>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>© 2025 Progress Digital. Todos los derechos reservados. • Jalisco, México</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
